/**
 * Where the site contact's no-login page lives, in one place.
 *
 * Four things have to agree on this path and none of them can see the others' copy of it: the router
 * (`app.routes.ts`), the shell's layout branch (`app.component.ts`), the idle-timeout guard
 * (`idle-timeout.service.ts`), and nginx's `Referrer-Policy` map (`charts/default.conf`). Three of
 * them import from here; the fourth is a comment in `charts/default.conf` pointing back at this file,
 * because an nginx config cannot import anything.
 *
 * 🔴 **The token is a path segment here and nowhere else.** The emailed link is a URL, so the token
 * has to travel in one — that is unavoidable for a link. What is avoidable is repeating it on the
 * wire: every API call this page makes forwards the token in the `X-Site-Visit-Token` header
 * (`SITE_VISIT_TOKEN_HEADER`), so it never reaches an access log, and the route carries a
 * `no-referrer` policy so it never reaches a third-party asset's `Referer` either.
 *
 * ⚠️ **Task 11's outreach email must compose exactly this path.** Nothing in connect-service builds
 * the link yet (grep confirms: no SPA URL is composed anywhere on that side), so this file is the
 * definition rather than a copy of one. If the email builder lands on a different path, change it
 * here too — a mismatch is a dead link for every site contact, and no test on either side would see
 * it.
 */

/** The router's path, relative — no leading slash, `:token` as the parameter. */
export const PUBLIC_SITE_VISIT_RESPOND_ROUTE = 'site-visit/respond/:token';

/** The absolute URL prefix, trailing slash included, for prefix matching against `Router.url`. */
export const PUBLIC_SITE_VISIT_RESPOND_PREFIX = '/site-visit/respond/';

/**
 * Whether a URL addresses the public site-contact page. Matched on the path prefix rather than by
 * exact string, because the token is a path segment; the query string and fragment are irrelevant
 * either way, since the prefix sits ahead of both.
 */
export function isPublicSiteVisitRespondUrl(url: string | null | undefined): boolean {
  return !!url && url.split(/[?#]/)[0].startsWith(PUBLIC_SITE_VISIT_RESPOND_PREFIX);
}
